import ajax from '@/lib/ajax';
import { VuexModule, Module, Mutation, Action, getModule } from 'vuex-module-decorators';
import Customer from '@/models/Customers/Customers/Customer';
import store from '@/store';
import { AppModule } from '@/store/Auth/AppModule';
import CustomerAccount from '@/models/Accounts/DepositAccounts/DepositAccount';
import CreateCustomerAccountRequest from '@/models/Customers/Customers/CreateCustomerAccountRequest';
import PermissionUtils from '@/lib/permissionUtils';
import ValidationUtils from '@/lib/validationUtils';

export interface ICustomerState {
    loading: boolean;
    loadingAccounts: boolean;
    loadingNewCustomerAccount: boolean;
    customer: Customer | null;
    customers: Customer[];
    customersSearch: string;
    totalCustomersPages: number;
    accounts: CustomerAccount[];
    totalDepositAccountsPages: number;
    newCustomerAccountID: string;
    pageSize: number;
}

@Module({ dynamic: true, store, name: 'CustomersModule', namespaced: true })
class CustomerStore extends VuexModule implements ICustomerState {

    public loading: boolean = true;
    public loadingAccounts: boolean = true;
    public loadingNewCustomerAccount: boolean = false;
    public customer: Customer = new Customer();
    public customers: Customer[] = [];
    public customersSearch: string = '';
    public totalCustomersPages: number = 1;
    public accounts: CustomerAccount[] = [];
    public totalDepositAccountsPages: number = 1;
    public newCustomerAccountID: string = null;
    public pageSize: number = PermissionUtils.getPageSize();

    @Action
    async getCustomers(pageNumber: number) {

        this.setLoading(true);

        const searchParameter = this.customersSearch ? `&name=${ValidationUtils.encodeRFC5987ValueChars(this.customersSearch)}` : '';

        await ajax.get(`/portal-backend/ux-api/customers?pageSize=${this.pageSize}&pageNumber=${pageNumber}${searchParameter}`)
        .then(response => {
            this.context.commit("setCustomers", response.data.results);
            this.context.commit("setCustomersTotalPages", response.data.records.totalRecords);
            this.setLoading(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoading(false);
        });
    }

    @Action
    async updateCustomersSearch(searchText: string) {
        // For a smoother UI experience we immidiately set the loading flag
        // and then reset the customers and page count all before
        // searching, this makes the UI look faster and avoids old data hanging around
        this.setLoading(true);
        this.context.commit("resetCustomers");
        this.context.commit("setCustomersTotalPages", 1);
        // Then set the search text
        this.context.commit("setCustomersSearch", searchText);
    }

    @Action
    async getCustomer(id: string) {

        this.setLoading(true);

        await ajax.get(`/portal-backend/ux-api/customers/${id}`)
        .then(response => {
            this.context.commit("setCustomer", response.data);
            this.setLoading(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoading(false);
        });
    }

    @Action
    async getAccountsForCustomer(data: {id: string, pageNumber: number}) {

        this.setLoadingAccounts(true);

        await ajax.get(`/portal-backend/ux-api/accounts?pageSize=${this.pageSize}&pageNumber=${data.pageNumber}&customerId=${data.id}`)
        .then(response => {
            this.context.commit("setAccounts", response.data.accounts);
            this.context.commit("setCustomerAccountsTotalPages", response.data.records.totalRecords);
            this.setLoadingAccounts(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoadingAccounts(false);
        });
    }

    @Action
    public async createCustomerAccount(request: CreateCustomerAccountRequest) {

        this.setLoadingNewCustomerAccount(true);

        await ajax.post(`/portal-backend/ux-api/accounts/`, request)
        .then(response => {
            this.context.commit("setNewCustomerAccountID", response.data.id);
            this.setLoadingNewCustomerAccount(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoadingNewCustomerAccount(false);
        });
    }

    @Action
    public async cleanState() {
        this.context.commit("resetState");
    }

    @Mutation
    resetState() {
        this.loading = true;
        this.loadingAccounts = false;
        this.loadingNewCustomerAccount = false;
        this.customer = new Customer();
        this.customers = [];
        this.customersSearch = '';
        this.totalCustomersPages = 1;
        this.accounts = [];
        this.totalDepositAccountsPages = 1;
        this.newCustomerAccountID = null;
    }

    @Mutation
    private async setLoading(loading: boolean) {
        this.loading = loading;
    }

    @Mutation
    private async setLoadingAccounts(loading: boolean) {
        this.loadingAccounts = loading;
    }

    @Mutation
    private async setLoadingNewCustomerAccount(loading: boolean) {
        this.loadingNewCustomerAccount = loading;
    }

    @Mutation
    private async setCustomer(customer: Customer) {
        this.customer = customer;
    }

    @Mutation
    private async setCustomers(response: Customer[]) {
        if (response) {
            this.customers = response;
        }
    }

    @Mutation
    private async resetCustomers() {
        this.customers = [];
    }

    @Mutation
    private async setCustomersSearch(text: string) {
        this.customersSearch = text;
    }

    @Mutation
    private async setCustomersTotalPages(total: number) {
        this.totalCustomersPages = Math.ceil(total/this.pageSize);
    }

    @Mutation
    private async setAccounts(response: CustomerAccount[]) {
        if (response) {
            this.accounts = response;
        }
    }

    @Mutation
    private async setCustomerAccountsTotalPages(total: number) {
        this.totalDepositAccountsPages = Math.ceil(total/this.pageSize);
    }

    @Mutation
    private async setNewCustomerAccountID(newCustomerAccountID: string) {
        this.newCustomerAccountID = newCustomerAccountID;
    }

}

export const CustomerModule = getModule(CustomerStore);